/**
 * ════════════════════════════════════════════════════════════════════════════
 * RRHH UP SOLUTIONS — MOTOR DE CÁLCULO PERUANO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * SUELDO:
 *   Sueldo base completo − descuento faltas − descuento tardanzas
 *
 * GRATIFICACIÓN (Ley 27735):
 *   Semestre jul: meses trabajados entre enero y junio (máx 6)
 *   Semestre dic: meses trabajados entre julio y diciembre (máx 6)
 *   Monto = (sueldo_base + asig_familiar) × meses_semestre / 6
 *   Régimen general: 100% · Pequeña empresa: 50% · Microempresa: 0%
 *   Requiere mínimo 1 mes completo en el semestre.
 *
 * CTS (D.Leg. 650):
 *   Provisión mensual = remuneración_computable / 12
 *   Remuneración computable = sueldo + asig_familiar + gratif_mensualizada(1/6)
 *   Se provisiona solo por los meses trabajados desde hire_date.
 *   Régimen general: 100% · Pequeña empresa: 50% · Microempresa: 0%
 *
 * PENSIÓN (ONP/AFP) — configurable por empleado:
 *   Cada empleado puede tener su propio sistema y tasa.
 *   Si no tiene configurado uno propio, hereda el de la empresa.
 *
 * SEGURO DE SALUD — configurable por empleado:
 *   EsSalud (9% empleador) o SIS (monto fijo referencial).
 *   Si no tiene configurado, hereda el de la empresa.
 * ════════════════════════════════════════════════════════════════════════════
 */

import { Company, Employee, Attendance, Bonus } from './supabase';

export interface PayrollCalculationInput {
  employee: Employee;
  company: Company;
  periodYear: number;
  periodMonth: number;
  attendanceRecords: Attendance[];
  bonuses: Bonus[];
  workingDaysInMonth: number;
}

export interface PayrollCalculationResult {
  baseSalary: number;
  daysWorked: number;
  absentDays: number;
  proportionalBase: number;
  overtimeHours: number;
  overtimeAmount: number;
  familyAssignment: number;
  gratification: number;
  gratificationMonths: number;        // meses del semestre que cuenta
  bonificacionExtraordinaria: number; // 9% de la gratificación (Ley 29351)
  bonusesTotal: number;
  grossSalary: number;
  absenceDeduction: number;
  tardinessDeduction: number;
  lateMinutesTotal: number;
  onpAmount: number;
  afpAmount: number;
  incomeTax: number;
  totalWorkerDeductions: number;
  essaludAmount: number;
  ctsProvision: number;
  ctsMonthsWorked: number;            // meses trabajados desde ingreso
  totalDeductions: number;
  netSalary: number;
  pensionSystem: string;              // 'onp' | 'afp' | 'ninguno'
  healthSystem: string;               // 'essalud' | 'sis'
  regimeApplied: string;
  calculationNotes: Record<string, any>;
}

// ════════════════════════════════════════════════════════════════════════════
// HELPERS DE FECHA
// ════════════════════════════════════════════════════════════════════════════

/**
 * Calcula meses completos trabajados entre hire_date y el período procesado.
 * Incluye el mes del período si el empleado ya trabajó ese mes.
 * Mínimo 0, sin tope (se topa externamente según necesidad).
 */
function monthsWorkedUntil(hireDateStr: string, periodYear: number, periodMonth: number): number {
  const hire = new Date(hireDateStr + 'T12:00:00');
  const hireYear  = hire.getFullYear();
  const hireMonth = hire.getMonth() + 1; // 1-12
  const hireDay   = hire.getDate();

  // Cuántos meses completos desde hire_date hasta el último día del período
  let months = (periodYear - hireYear) * 12 + (periodMonth - hireMonth);

  // Si ingresó después del día 1, el primer mes puede ser incompleto.
  // Ley: se necesita haber laborado más de la mitad del mes para que cuente completo.
  // Simplificación estándar: si ingresó en la primera quincena (día 1–15) cuenta el mes,
  // si ingresó en la segunda quincena (16–31) no cuenta ese mes inicial.
  if (hireDay > 15) months = Math.max(0, months - 1);

  return Math.max(0, months + 1); // +1 porque incluye el mes actual
}

/**
 * Calcula los meses del semestre de gratificación que el empleado tiene derecho.
 *
 * Semestre JULIO  : enero (1) → junio (6), máximo 6 meses
 * Semestre DICIEM.: julio (7) → diciembre (12), máximo 6 meses
 *
 * Retorna número de meses (puede ser fraccionario por días, pero usamos enteros
 * por quincenas: cada 15 días = 0.5 mes → redondeamos al 0.5 más cercano).
 */
function gratificationSemesterMonths(
  hireDateStr: string,
  periodYear: number,
  periodMonth: number
): number {
  if (!isGratificationMonth(periodMonth)) return 0;

  const hire = new Date(hireDateStr + 'T12:00:00');
  const hireYear  = hire.getFullYear();
  const hireMonth = hire.getMonth() + 1;
  const hireDay   = hire.getDate();

  // Rango del semestre
  const semStart = periodMonth === 7 ? 1 : 7;  // ene o jul
  const semEnd   = periodMonth === 7 ? 6 : 12; // jun o dic
  const semStartYear = periodYear;

  // Si el empleado ingresó después del cierre del semestre → 0
  const hireAbsMonth = hireYear * 12 + hireMonth;
  const semEndAbsMonth = semStartYear * 12 + semEnd;
  if (hireAbsMonth > semEndAbsMonth) return 0;

  // Primer mes del semestre en que el empleado trabajó
  const semStartAbsMonth = semStartYear * 12 + semStart;
  const effectiveStart = Math.max(hireAbsMonth, semStartAbsMonth);

  // Meses completos = desde effectiveStart hasta semEnd inclusive
  let months = semEndAbsMonth - effectiveStart + 1;

  // Ajuste por quincena de ingreso en el primer mes efectivo
  if (hireAbsMonth === effectiveStart && hireDay > 15) {
    months -= 0.5; // ingresó en segunda quincena: ese mes cuenta medio
  }

  return Math.max(0, Math.min(6, months));
}

// ════════════════════════════════════════════════════════════════════════════
// CÁLCULOS DE BENEFICIOS
// ════════════════════════════════════════════════════════════════════════════

export function isGratificationMonth(month: number): boolean {
  return month === 7 || month === 12;
}

/**
 * Gratificación proporcional según meses trabajados en el semestre (Ley 27735)
 * Base = sueldo_base + asignación_familiar
 * Monto = base × (meses_semestre / 6)
 * Régimen general: 100% · Pequeña empresa: 50% · Microempresa: 0%
 */
export function calculateGratification(
  baseSalary: number,
  familyAssignment: number,
  regime: string,
  hireDateStr: string,
  periodYear: number,
  periodMonth: number
): { amount: number; months: number } {
  if (!isGratificationMonth(periodMonth)) return { amount: 0, months: 0 };

  const months = gratificationSemesterMonths(hireDateStr, periodYear, periodMonth);
  if (months <= 0) return { amount: 0, months: 0 };

  const base = baseSalary + familyAssignment;
  let factor = 0;
  switch (regime) {
    case 'general':         factor = 1.0; break;
    case 'pequeña_empresa': factor = 0.5; break;
    case 'microempresa':    factor = 0.0; break;
    default:                factor = 1.0;
  }

  const amount = Math.round(base * (months / 6) * factor * 100) / 100;
  return { amount, months };
}

/**
 * CTS mensual provisionada (D.Leg. 650)
 * Remuneración computable = sueldo + asig_familiar + (gratif_anual / 12)
 * Gratif mensualizada = sueldo_base / 6 (equivalente a 1/6 de una gratif)
 * CTS mensual = rem_computable / 12
 * Solo aplica si el empleado lleva más de 1 mes trabajando.
 * Régimen general: 100% · Pequeña empresa: 50% · Microempresa: 0%
 */
export function calculateCTSProvision(
  baseSalary: number,
  familyAssignment: number,
  regime: string,
  hireDateStr: string,
  periodYear: number,
  periodMonth: number
): { amount: number; monthsWorked: number } {
  const monthsWorked = monthsWorkedUntil(hireDateStr, periodYear, periodMonth);

  // La CTS aplica si el empleado tiene al menos 1 mes completo trabajado
  if (monthsWorked < 1) return { amount: 0, monthsWorked };

  let factor = 0;
  switch (regime) {
    case 'general':         factor = 1.0; break;
    case 'pequeña_empresa': factor = 0.5; break;
    case 'microempresa':    factor = 0.0; break;
    default:                factor = 1.0;
  }
  if (factor === 0) return { amount: 0, monthsWorked };

  // Remuneración computable = sueldo + asig_familiar + 1/6 de una gratificación
  const gratifMensualizada = baseSalary / 6;
  const remComputable = baseSalary + familyAssignment + gratifMensualizada;
  const amount = Math.round((remComputable / 12) * factor * 100) / 100;

  return { amount, monthsWorked };
}

export function calculateFamilyAssignment(employee: Employee, company: Company): number {
  if (!company.assign_familiar) return 0;
  if (!employee.has_family_assignment) return 0;
  if (company.regime === 'microempresa') return 0;
  return Math.round(company.rmv * 0.10 * 100) / 100;
}

/**
 * Impuesto 5ta categoría (Art. 53 LIR).
 * Tramos sobre la renta neta = renta bruta anual − 7 UIT.
 * Escala: 0-5 UIT → 8% | 5-20 → 14% | 20-35 → 17% | 35-45 → 20% | >45 → 30%
 * UIT se obtiene de company.uit para que sea actualizable sin tocar código.
 */
export function calculateIncomeTax(annualGross: number, uit: number = 5500): number {
  const taxableIncome = annualGross - (7 * uit);
  if (taxableIncome <= 0) return 0;

  const brackets = [
    { limit: 5  * uit, rate: 0.08 },
    { limit: 20 * uit, rate: 0.14 },
    { limit: 35 * uit, rate: 0.17 },
    { limit: 45 * uit, rate: 0.20 },
    { limit: Infinity, rate: 0.30 },
  ];

  let tax = 0, remaining = taxableIncome, prev = 0;
  for (const b of brackets) {
    const bucket = Math.min(remaining, b.limit - prev);
    if (bucket <= 0) break;
    tax += bucket * b.rate;
    remaining -= bucket;
    prev = b.limit;
    if (remaining <= 0) break;
  }
  return Math.round((tax / 12) * 100) / 100;
}

// ════════════════════════════════════════════════════════════════════════════
// FUNCIÓN PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
export function calculateEmployeePayroll(input: PayrollCalculationInput): PayrollCalculationResult {
  const { employee, company, periodYear, periodMonth, attendanceRecords, bonuses, workingDaysInMonth } = input;

  const notes: Record<string, any> = {
    regime: company.regime,
    rmv: company.rmv,
    workingDaysInMonth,
    hireDate: employee.hire_date,
  };

  // ── 1. Sistema pensionario del empleado ───────────────────────────────────
  // El empleado puede tener su propio sistema y tasa. Si no, hereda la empresa.
  const pensionSystem: string = (employee as any).pension_system || company.afp_system || 'onp';
  const pensionRate: number   = (employee as any).pension_rate   || company.snp_rate   || 13;
  const healthSystem: string  = (employee as any).health_system  || 'essalud';

  notes.pensionSystem = pensionSystem;
  notes.pensionRate   = pensionRate;
  notes.healthSystem  = healthSystem;

  // ── 2. Asistencia ────────────────────────────────────────────────────────
  const absentDays = attendanceRecords.filter(a => a.status === 'absent').length;
  const workedStatuses = ['present', 'late', 'vacation', 'sick_leave', 'permission'];
  const daysWorked = attendanceRecords.length > 0
    ? attendanceRecords.filter(a => workedStatuses.includes(a.status)).length
    : workingDaysInMonth;

  const totalLateMinutes    = attendanceRecords.reduce((s, a) => s + (a.late_minutes || 0), 0);
  const totalOvertimeMinutes = attendanceRecords.reduce((s, a) => s + (a.overtime_minutes || 0), 0);
  const overtimeHours = Math.round((totalOvertimeMinutes / 60) * 100) / 100;

  notes.daysWorked  = daysWorked;
  notes.absentDays  = absentDays;
  notes.lateMinutes = totalLateMinutes;

  // ── 3. Sueldo base − descuentos asistencia ────────────────────────────────
  const dailySalary = employee.base_salary / workingDaysInMonth;
  const absenceDeduction    = Math.round(dailySalary * absentDays * 100) / 100;
  const dailyMinutes        = company.daily_work_hours * 60;
  const tardinessDeduction  = totalLateMinutes > 0
    ? Math.round(dailySalary * (totalLateMinutes / dailyMinutes) * 100) / 100
    : 0;
  const adjustedBase = Math.round(
    (employee.base_salary - absenceDeduction - tardinessDeduction) * 100
  ) / 100;

  notes.dailySalary        = dailySalary.toFixed(2);
  notes.absenceDeduction   = absenceDeduction;
  notes.tardinessDeduction = tardinessDeduction;
  notes.adjustedBase       = adjustedBase;

  // ── 4. Asignación familiar ────────────────────────────────────────────────
  const familyAssignment = calculateFamilyAssignment(employee, company);
  notes.familyAssignment = familyAssignment;

  // ── 5. Gratificación proporcional (Ley 27735) ─────────────────────────────
  const gratifResult = calculateGratification(
    employee.base_salary,
    familyAssignment,
    company.regime,
    employee.hire_date,
    periodYear,
    periodMonth
  );
  const gratification       = gratifResult.amount;
  const gratificationMonths = gratifResult.months;

  notes.gratification = `S/ ${gratification.toFixed(2)} (${gratificationMonths} meses del semestre)`;

  // ── 5b. Bonificación Extraordinaria (Ley 29351) — 9% de la gratificación ─
  // No afecta AFP/ONP, SÍ afecta 5ta categoría, NO afecta EsSalud.
  const bonificacionExtraordinaria = gratification > 0
    ? Math.round(gratification * 0.09 * 100) / 100
    : 0;
  if (bonificacionExtraordinaria > 0) {
    notes.bonificacionExtraordinaria = `S/ ${bonificacionExtraordinaria.toFixed(2)} (9% de S/ ${gratification.toFixed(2)})`;
  }

  // ── 6. Horas extra ────────────────────────────────────────────────────────
  const hourlyRate     = dailySalary / company.daily_work_hours;
  const overtimeAmount = Math.round(overtimeHours * hourlyRate * 1.25 * 100) / 100;

  // ── 7. Bonos ──────────────────────────────────────────────────────────────
  let bonusesTotal = 0;
  const bonusDetails: any[] = [];
  for (const bonus of bonuses) {
    if (!bonus.is_active) continue;
    const amount = bonus.type === 'fixed'
      ? bonus.value
      : bonus.type === 'percentage'
        ? Math.round((employee.base_salary * bonus.value) / 100 * 100) / 100
        : 0;
    bonusesTotal += amount;
    bonusDetails.push({ name: bonus.name, amount });
  }
  notes.bonusDetails = bonusDetails;

  // ── 8. Bruto ──────────────────────────────────────────────────────────────
  // Incluye bonificación extraordinaria: SÍ afecta 5ta categoría (D.U. 012-2010).
  const grossSalary = Math.round(
    (adjustedBase + overtimeAmount + familyAssignment + gratification + bonificacionExtraordinaria + bonusesTotal) * 100
  ) / 100;

  // ── 9. ONP / AFP (por empleado) ───────────────────────────────────────────
  const pensionBase = adjustedBase + familyAssignment;
  let onpAmount = 0, afpAmount = 0;

  if (pensionSystem === 'onp') {
    onpAmount = Math.round(pensionBase * (pensionRate / 100) * 100) / 100;
    notes.pension = `ONP ${pensionRate}% sobre S/ ${pensionBase.toFixed(2)}`;
  } else if (pensionSystem === 'afp') {
    // AFP: 10% aporte obligatorio + 1.37% prima seguro = 11.37% total (tasa fija legal)
    const afpAporte = Math.round(pensionBase * 0.10 * 100) / 100;
    const afpPrima  = Math.round(pensionBase * 0.0137 * 100) / 100;
    afpAmount = Math.round((afpAporte + afpPrima) * 100) / 100;
    notes.pension = `AFP 11.37% → Aporte oblig. 10% (S/ ${afpAporte.toFixed(2)}) + Prima seguro 1.37% (S/ ${afpPrima.toFixed(2)}) sobre S/ ${pensionBase.toFixed(2)}`;
  } else {
    notes.pension = 'Sin aporte pensionario';
  }

  // ── 10. Impuesto 5ta categoría ────────────────────────────────────────────
  // La gratificación y bonif. extraordinaria se pagan solo en julio y diciembre
  // (2 veces al año), por eso NO se multiplican ×12 sino ×2 en la proyección anual.
  const regularMonthly = adjustedBase + overtimeAmount + familyAssignment + bonusesTotal;
  const annualGross5ta = regularMonthly * 12
    + (gratification + bonificacionExtraordinaria) * 2;
  const uit = company.uit ?? 5500;
  const incomeTax = calculateIncomeTax(annualGross5ta, uit);
  notes.incomeTax = `Proyección anual: S/ ${annualGross5ta.toFixed(2)} (base regular ×12 + gratif ×2) → S/ ${incomeTax.toFixed(2)}/mes`;

  const totalWorkerDeductions = onpAmount + afpAmount + incomeTax;

  // ── 11. EsSalud / SIS (por empleado) ─────────────────────────────────────
  let essaludAmount = 0;
  if (healthSystem === 'essalud') {
    essaludAmount = Math.round(adjustedBase * (company.essalud_rate / 100) * 100) / 100;
    notes.essalud = `EsSalud ${company.essalud_rate}% sobre S/ ${adjustedBase.toFixed(2)}`;
  } else {
    // SIS Microempresa (Ley MYPE): aporte empleador ~S/ 15/mes (Estado subsidia el otro 50%)
    essaludAmount = 15.00;
    notes.essalud = 'SIS Microempresa (aporte empleador S/ 15.00)';
  }

  // ── 12. CTS proporcional (D.Leg. 650) ────────────────────────────────────
  const ctsResult = calculateCTSProvision(
    employee.base_salary,
    familyAssignment,
    company.regime,
    employee.hire_date,
    periodYear,
    periodMonth
  );
  const ctsProvision    = ctsResult.amount;
  const ctsMonthsWorked = ctsResult.monthsWorked;
  notes.cts = `S/ ${ctsProvision.toFixed(2)} (${ctsMonthsWorked} meses trabajados)`;

  // ── 13. Neto ──────────────────────────────────────────────────────────────
  const totalDeductions = totalWorkerDeductions;
  const netSalary       = Math.round((grossSalary - totalDeductions) * 100) / 100;

  return {
    baseSalary: employee.base_salary,
    daysWorked,
    absentDays,
    proportionalBase: adjustedBase,
    overtimeHours,
    overtimeAmount,
    familyAssignment,
    gratification,
    gratificationMonths,
    bonificacionExtraordinaria,
    bonusesTotal,
    grossSalary,
    absenceDeduction,
    tardinessDeduction,
    lateMinutesTotal: totalLateMinutes,
    onpAmount,
    afpAmount,
    incomeTax,
    totalWorkerDeductions,
    essaludAmount,
    ctsProvision,
    ctsMonthsWorked,
    totalDeductions,
    netSalary,
    pensionSystem,
    healthSystem,
    regimeApplied: company.regime,
    calculationNotes: notes,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// INDEMNIZACIÓN (D.Leg. 728)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Indemnización por despido arbitrario.
 * Régimen General: 1.5 remuneraciones × año, tope 12 remuneraciones.
 * Pequeña Empresa: 1 remuneración × año, tope 6.
 * Microempresa: no aplica.
 * RC = sueldo + asig. familiar (sin 1/6 gratif, per Ley).
 * No afecta AFP/ONP, ni 5ta categoría, ni EsSalud.
 */
export function calculateIndemnizacion(
  baseSalary: number,
  familyAssignment: number,
  regime: string,
  hireDateStr: string,
  dismissalDateStr?: string
): { amount: number; years: number; months: number } {
  if (regime === 'microempresa') return { amount: 0, years: 0, months: 0 };

  const hire = new Date(hireDateStr + 'T12:00:00');
  const dismissal = dismissalDateStr
    ? new Date(dismissalDateStr + 'T12:00:00')
    : new Date();

  const totalMonths = Math.max(
    0,
    (dismissal.getFullYear() - hire.getFullYear()) * 12 +
    (dismissal.getMonth() - hire.getMonth())
  );
  const years  = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const rc = baseSalary + familyAssignment;

  let indemnPerYear: number;
  let cap: number;
  if (regime === 'pequeña_empresa') {
    // D.Leg. 1086: 20 remuneraciones diarias por año, tope 120 remuneraciones diarias
    // 20 días = 20/30 de una remuneración mensual; 120 días = 4 remuneraciones mensuales
    indemnPerYear = (20 / 30) * rc;
    cap           = 4 * rc;
  } else {
    // D.Leg. 728: 1.5 remuneraciones mensuales por año, tope 12 remuneraciones
    indemnPerYear = 1.5 * rc;
    cap           = 12 * rc;
  }

  const raw = indemnPerYear * years + (indemnPerYear / 12) * months;
  const amount = Math.min(Math.round(raw * 100) / 100, cap);

  return { amount, years, months };
}

// ════════════════════════════════════════════════════════════════════════════
// UTILIDADES
// ════════════════════════════════════════════════════════════════════════════
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency', currency: 'PEN', minimumFractionDigits: 2,
  }).format(amount);
}

export function getMonthName(month: number): string {
  return ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
          'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][month - 1] || '';
}

export function getRegimeName(regime: string): string {
  return ({
    general: 'Régimen General',
    pequeña_empresa: 'REMYPE - Pequeña Empresa',
    microempresa: 'REMYPE - Microempresa',
  } as Record<string,string>)[regime] || regime;
}
